// monitor 的统一分类协议都定义在这里。
// 目标很简单：让所有上报事件都有稳定的命名、标签和来源定位方式，
// 避免每个页面、每个 store、每个 infra 模块各写一套字段。
export const MONITOR_SOURCE = Object.freeze({
  // 运行时异常，例如 Vue errorHandler 捕获到的错误。
  RUNTIME: 'runtime',
  // HTTP 请求层异常，例如慢请求、5xx、网络错误。
  REQUEST: 'request',
  // WebSocket 基础设施层异常，例如断线、重连失败、消息解析失败。
  SOCKET: 'socket',
  // 业务域异常，例如行情流断更、拉单失败、撤单失败。
  BUSINESS: 'business',
})

// 业务域分类。
// 只有 source=business 时才会用到，用来区分是哪一块业务在上报。
export const BUSINESS_DOMAIN = Object.freeze({
  // 行情域，主要处理 ticker / depth / trade_history 等行情链路健康度。
  MARKET: 'market',
  // 账户域，主要处理资产、账户快照相关失败。
  ACCOUNT: 'account',
  // 现货交易域，主要处理现货委托、撤单等动作或快照异常。
  TRADE: 'trade',
  // 合约交易域，主要处理仓位、保证金、合约委托等异常。
  FUTURES: 'futures',
})

// 统一事件类型枚举。
// 这里定义的是“异常类型”，不是具体页面，也不是具体接口名。
// 具体资源名、动作名会放到 monitorPoint 或 extra 里补充。
export const MONITOR_EVENT = Object.freeze({
  // Vue 运行时错误。
  VUE_ERROR: 'vue_error',
  // 请求成功了，但耗时过长。
  SLOW_REQUEST: 'slow_request',
  // HTTP 5xx 类错误。
  HTTP_ERROR: 'http_error',
  // 网络错误，例如断网、超时、服务不可达。
  NETWORK_ERROR: 'network_error',
  // Socket 非主动断开。
  SOCKET_DISCONNECTED: 'socket_disconnected',
  // 浏览器底层抛出的 socket error 事件。
  SOCKET_ERROR: 'socket_error',
  // 自动重连最终失败。
  SOCKET_RECONNECT_FAILED: 'socket_reconnect_failed',
  // 原始消息解析失败。
  SOCKET_PARSE_ERROR: 'socket_parse_error',
  // 收到消息后，业务回调执行时报错。
  SOCKET_EMIT_ERROR: 'socket_emit_error',
  // 行情流长时间没更新，例如 ticker/depth/trade_history 断更。
  STREAM_STALE: 'stream_stale',
  // 快照型失败，例如列表拉取失败、行情快照失败、资产获取失败。
  SNAPSHOT_FAILURE: 'snapshot_failure',
  // 动作型失败，例如撤单失败、批量撤单失败、下单失败。
  ACTION_FAILURE: 'action_failure',
})

// 统一记录“当前监控点是从哪个文件接入的”。
// 这个字段会进入 tags，目的是让告警可以直接反查到代码入口。
// 这里只放 monitor 接入方，不放接口路径、后端模块名之类的非前端入口信息。
export const MONITOR_PATH = Object.freeze({
  GLITCHTIP: 'src/utils/glitchtip.js',
  REQUEST_INFRA: 'src/utils/monitor/infra/request.js',
  SOCKET_INFRA: 'src/utils/monitor/infra/socket.js',
  DOMAIN_MARKET: 'src/utils/monitor/domain/market.js',
  DOMAIN_BUSINESS: 'src/utils/monitor/domain/business.js',
  STORE_USER: 'src/store/user.js',
  STORE_FUTURES: 'src/store/futures.js',
  VIEW_TRADE: 'src/view/trade/Trade.vue',
  VIEW_FUTURES: 'src/view/futures/Futures.vue',
  VIEW_TRADE_ORDERS: 'src/view/trade/components/TradeOrders.vue',
  VIEW_TRADE_OPEN_ORDERS_TABLE: 'src/view/trade/components/table/OpenOrdersTable.vue',
  VIEW_FUTURES_TRADE_ORDERS: 'src/view/futures/components/TradeOrders/TradeOrders.vue',
})

// 统一生成监控事件标题。
// 规则：
// 1. 普通事件：source.event 或 source.event.point
// 2. 业务事件：source.domain.event 或 source.domain.event.point
// 这样可以保证 GlitchTip 里的事件名稳定、可聚合、可读。
export const getMonitorTitle = ({ monitorSource, monitorEvent, businessDomain, monitorPoint }) => {
  if (monitorSource === MONITOR_SOURCE.BUSINESS && businessDomain) {
    return monitorPoint
      ? `${monitorSource}.${businessDomain}.${monitorEvent}.${monitorPoint}`
      : `${monitorSource}.${businessDomain}.${monitorEvent}`
  }

  return monitorPoint
    ? `${monitorSource}.${monitorEvent}.${monitorPoint}`
    : `${monitorSource}.${monitorEvent}`
}

// 统一生成结构化 tags。
// title 适合人看，tags 适合筛选、聚合、做统计。
export const getMonitorTags = ({ monitorSource, monitorEvent, monitorPath, businessDomain }) => {
  return {
    monitor_source: monitorSource,
    business_domain: businessDomain,
    monitor_event: monitorEvent,
    monitor_path: monitorPath,
  }
}

// descriptor 是 title + tags 的组合结果。
// 上层一般直接拿这个结果继续往 capture 里传，避免每次手动拼两遍。
export const getMonitorDescriptor = ({ monitorSource, monitorEvent, monitorPath, businessDomain, monitorPoint }) => {
  return {
    title: getMonitorTitle({ monitorSource, monitorEvent, businessDomain, monitorPoint }),
    tags: getMonitorTags({ monitorSource, monitorEvent, monitorPath, businessDomain }),
  }
}

// 统一提取错误对象里的基础信息。
// 这样所有 error 类事件都会带上 error_name / error_message，
// 调用方再额外补 resource、action、symbol、status 等业务上下文。
export const getMonitorErrorExtra = (error, extra = {}) => {
  return {
    error_name: error?.name ?? '',
    error_message: error?.message ?? String(error ?? ''),
    ...extra,
  }
}
